import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface StayComandaLine {
  id: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

export interface UpdateStayComandaLineInput {
  reservationId: string;
  lineId: string;
  quantity: number;
  unitPrice: number;
}

export interface StayComanda {
  saleId: string;
  total: number;
  lines: StayComandaLine[];
}

/**
 * A base (sem `includeClosed`) é usada para invalidar depois de mutações —
 * `invalidateQueries` casa por prefixo, então isso alcança as duas variantes.
 * A query em si precisa do terceiro elemento: sem ele, uma leitura com
 * `includeClosed=false` (ex.: o hook solto em `FollowUpDialog` só para pegar
 * o `saleId`) e outra com `includeClosed=true` (a comanda somente leitura)
 * caem na mesma chave e uma rouba o cache da outra — foi por isso que a
 * comanda paga não aparecia depois do checkout.
 */
export const stayComandaKey = (reservationId: string | null, includeClosed = false) => [
  "stay-comanda",
  reservationId,
  includeClosed,
];

interface SaleRow {
  id: string;
  total: number | null;
  sale_items: Array<{
    id: string;
    item_name: string;
    quantity: number;
    unit_price: number;
    line_total: number;
  }> | null;
}

/**
 * A comanda da estadia é a venda POS ABERTA ligada à reserva — a mesma que
 * `useOpenStayComanda` cria quando a reserva nasce. Aqui é só leitura.
 *
 * `sales`/`sale_items` não estão tipadas em `src/integrations/supabase/types.ts`,
 * por isso o cast `as any`, no mesmo padrão de `useOpenStayComanda.ts`.
 */
export function useStayComanda(reservationId: string | null, includeClosed = false) {
  return useQuery({
    queryKey: stayComandaKey(reservationId, includeClosed),
    enabled: !!reservationId,
    queryFn: async (): Promise<StayComanda | null> => {
      if (!reservationId) return null;
      const { data, error } = await (supabase as any)
        .from("sales")
        .select("id, total, sale_items ( id, item_name, quantity, unit_price, line_total )")
        .eq("stay_reservation_id", reservationId)
        .in("status", includeClosed ? ["ABERTA", "PAGA"] : ["ABERTA"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      const row = data as unknown as SaleRow | null;
      if (!row) return null;

      const lines = (row.sale_items ?? []).map((item) => ({
        id: item.id,
        itemName: item.item_name,
        quantity: item.quantity,
        unitPrice: item.unit_price,
        lineTotal: item.line_total,
      }));
      return {
        saleId: row.id,
        // O total da venda pode estar defasado se linhas foram inseridas
        // direto; a soma das linhas é o que a tela mostra.
        total: lines.reduce((sum, line) => sum + line.lineTotal, 0) || (row.total ?? 0),
        lines,
      };
    },
  });
}

export interface AddComandaLineInput {
  reservationId: string;
  saleId: string;
  itemName: string;
  unitPrice: number;
}

/**
 * Insere uma linha avulsa na comanda ABERTA da estadia. Exportada como função
 * autônoma (sem estado de hook) porque uma tarefa futura do Plano 2 (a
 * mutação de pernoite) vai chamá-la diretamente, fora de `useAddStayComandaLine`.
 */
export async function supabaseAddComandaLine({
  saleId,
  itemName,
  unitPrice,
}: {
  saleId: string;
  itemName: string;
  unitPrice: number;
}): Promise<{ id: string }> {
  const { data, error } = await (supabase as any).from("sale_items").insert({
    sale_id: saleId,
    item_name: itemName,
    quantity: 1,
    unit_price: unitPrice,
    line_total: unitPrice,
  }).select("id").single();
  if (error) throw error;
  return { id: data.id as string };
}

export function useAddStayComandaLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ saleId, itemName, unitPrice }: AddComandaLineInput) =>
      supabaseAddComandaLine({ saleId, itemName, unitPrice }),
    onSuccess: (_data, variables) => {
      // Prefixo sem `includeClosed`: invalida as duas variantes em cache (ver
      // o comentário em `stayComandaKey`).
      queryClient.invalidateQueries({ queryKey: ["stay-comanda", variables.reservationId] });
    },
  });
}

export function useUpdateStayComandaLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ lineId, quantity, unitPrice }: UpdateStayComandaLineInput) => {
      const { error } = await (supabase as any).from("sale_items").update({
        quantity,
        unit_price: unitPrice,
        line_total: quantity * unitPrice,
      }).eq("id", lineId);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["stay-comanda", variables.reservationId] }),
  });
}

export function useRemoveStayComandaLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reservationId: _reservationId, lineId }: { reservationId: string; lineId: string }) => {
      const { error } = await (supabase as any).from("sale_items").delete().eq("id", lineId);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["stay-comanda", variables.reservationId] }),
  });
}
